import { api } from './client';
import type { Patient, PatientListResponse, PatientFilters } from '@/types/patient';
import { createResourceCache } from './cache/resourceCache';
import {
  transformPatient,
  transformPatientAll,
  type ApiPatient,
} from './transforms/patientTransform';

const patientCache = createResourceCache<PatientListResponse>();
const patientAllCache = createResourceCache<PatientListResponse>();

export function clearPatientCache(): void {
  patientCache.clear();
  patientAllCache.clear();
}

async function fetchDoctorPatients(
  transform: (patient: ApiPatient) => Patient
): Promise<PatientListResponse> {
  const response = await api.get<ApiPatient[]>('/doctor/get-patient-list/');
  const patients = response.map(transform);

  return {
    patients,
    total: patients.length,
    page: 1,
    pageSize: 50,
  };
}

async function getCachedPatientList(
  cache: ReturnType<typeof createResourceCache<PatientListResponse>>,
  transform: (patient: ApiPatient) => Patient,
  forceRefresh: boolean
): Promise<PatientListResponse> {
  if (!forceRefresh) {
    const cached = cache.get();
    if (cached) {
      return cached;
    }

    const pending = cache.getPending();
    if (pending) {
      return pending;
    }
  }

  const request = fetchDoctorPatients(transform)
    .then((result) => {
      cache.set(result);
      return result;
    })
    .catch((error) => {
      cache.setPending(null);
      throw error;
    });

  cache.setPending(request);
  return request;
}

export async function getPatientList(
  _filters?: PatientFilters,
  forceRefresh = false
): Promise<PatientListResponse> {
  return getCachedPatientList(patientCache, transformPatient, forceRefresh);
}

export async function getPatientListAllActivities(
  _filters?: PatientFilters,
  forceRefresh = false
): Promise<PatientListResponse> {
  return getCachedPatientList(patientAllCache, transformPatientAll, forceRefresh);
}

export async function getPatientById(patientId: string): Promise<Patient | null> {
  const response = await getPatientList();
  return response.patients.find((p) => p.id === patientId) || null;
}

export async function getPatientByIdAllActivities(patientId: string): Promise<Patient | null> {
  const response = await getPatientListAllActivities();
  return response.patients.find((p) => p.id === patientId) || null;
}
